import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import {
  getProviderOverview,
  ProviderOverviewData,
  MetricPeriodType,
} from "api/Dashboard";
import { Panel, EmptyState, currency, Skeleton } from "./shared";

/**
 * The provider's overview (spec section 5).
 *
 * COST IS SAFE HERE, MARGIN IS NOT. Every figure on this screen is the
 * provider's OWN price - what we agreed to pay them, which they already
 * know. The client's price never reaches this endpoint, so margin
 * cannot be derived from it.
 *
 * THE QUEUE IS RANKED BY HOW SOON WORK STARTS, not by age. An
 * unaccepted PO with work in seven days outranks an older one with no
 * date: the provider's question is "what do I have to staff this week",
 * not "what has been sitting longest".
 *
 * Mobile-first is the stated priority for this audience (spec 7.14) -
 * providers are in the field. The layout here is single-column and
 * wraps rather than assuming a desktop width.
 */

const PERIODS: Array<{ key: MetricPeriodType; label: string }> = [
  { key: "month", label: "This month" },
  { key: "year", label: "This year" },
];

const ProviderOverview: React.FC = () => {
  const [period, setPeriod] = useState<MetricPeriodType>("year");
  const [data, setData] = useState<ProviderOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getProviderOverview(period);
        if (!cancelled) setData(result);
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? "This environment's backend does not have the provider overview yet."
              : "Your figures could not be loaded. Try again shortly.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const header = (
    <Flex
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "16px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          fontWeight: 600,
          color: "base_800",
        }}
      >
        Your work
        <Box
          as="span"
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            color: themevals.colors.dkgrey,
            marginLeft: "8px",
          }}
        >
          {data ? data.period.label : ""}
          {data && data.period.partial ? " to date" : ""}
        </Box>
      </Box>
      <Flex sx={{ gap: "4px" }}>
        {PERIODS.map((p) => {
          const active = p.key === period;
          return (
            <button
              key={p.key}
              type="button"
              disabled={loading}
              onClick={() => setPeriod(p.key)}
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: active ? 600 : 500,
                padding: "6px 12px",
                borderRadius: "6px",
                border: "1px solid " + themevals.colors.inputOutline,
                backgroundColor: active ? themevals.colors.base_800 : "#FFFFFF",
                color: active ? "#FFFFFF" : themevals.colors.dkgrey,
                cursor: loading ? "default" : "pointer",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </Flex>
    </Flex>
  );

  if (error) {
    return (
      <Box sx={{ marginBottom: "24px" }}>
        {header}
        <Box
          sx={{
            padding: "16px",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: themevals.colors.inputOutline,
            backgroundColor: "#FDF3F3",
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            color: themevals.colors.danger,
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  if (loading || !data) {
    return (
      <Box sx={{ marginBottom: "24px" }}>
        {header}
        <Skeleton height="140px" />
      </Box>
    );
  }

  const card = (title: string, value: string, sub: string) => (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: "1 1 240px",
        minWidth: "240px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "13px",
          fontWeight: 600,
          color: themevals.colors.dkgrey,
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "30px",
          fontWeight: 600,
          color: "base_800",
          lineHeight: 1.2,
          margin: "8px 0 6px",
        }}
      >
        {value}
      </Box>
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "12px",
          color: themevals.colors.dkgrey,
        }}
      >
        {sub}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ marginBottom: "24px" }}>
      {header}

      <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
        {card(
          "Work awarded",
          currency(data.awarded.value),
          data.awarded.count +
            (data.awarded.count === 1
              ? " purchase order"
              : " purchase orders"),
        )}
        {card(
          "Submitted, not yet paid",
          currency(data.awaitingPayment.value),
          data.awaitingPayment.count +
            (data.awaitingPayment.count === 1 ? " invoice" : " invoices"),
        )}
      </Flex>

      <Panel
        title="Needs your response"
        subtitle="Soonest work first, not oldest."
        sx={{ marginBottom: "24px" }}
      >
        {data.queue.length === 0 ? (
          <EmptyState
            headline="Nothing is waiting on you"
            detail="Purchase orders appear here as soon as they are sent, ordered by when the work starts."
          />
        ) : (
          data.queue.map((item) => (
            <Flex
              key={item.id}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "10px 0",
                borderTop: "1px solid",
                borderColor: themevals.colors.inputOutline,
                flexWrap: "wrap",
              }}
            >
              <Flex sx={{ gap: "10px", alignItems: "flex-start", minWidth: 0 }}>
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "3px",
                    backgroundColor:
                      item.severity === "critical"
                        ? themevals.colors.danger
                        : themevals.colors.warning,
                    marginTop: "6px",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "base_800",
                    }}
                  >
                    {item.title}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      color: themevals.colors.dkgrey,
                    }}
                  >
                    {item.reason}
                  </Box>
                </Box>
              </Flex>
              <Flex sx={{ alignItems: "center", gap: "12px", flexShrink: 0 }}>
                {item.amount !== null && (
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "base_800",
                    }}
                  >
                    {currency(item.amount)}
                  </Box>
                )}
                <button
                  type="button"
                  onClick={() =>
                    item.entityId
                      ? navigate("/purchase-orders/" + item.entityId)
                      : navigate("/purchase-orders")
                  }
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    padding: "5px 12px",
                    borderRadius: "6px",
                    border: "1px solid " + themevals.colors.inputOutline,
                    backgroundColor: "#FFFFFF",
                    color: themevals.colors.base_800,
                    cursor: "pointer",
                  }}
                >
                  {item.actionLabel}
                </button>
              </Flex>
            </Flex>
          ))
        )}
      </Panel>
    </Box>
  );
};

export default ProviderOverview;
